import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  DateRange,
  FormDataWithDateRange,
  generateAdvancedSearchParams,
  generateBasicSearchParams
} from 'app/book/components/TimeStep/components/AdvancedSearch/helpers';

import { setDepositAmount } from 'store/reducers/bookingAppointmentSlice';
import { setIsServicesLoading } from 'store/reducers/servicesSlice';

import {
  AppointmentDataInterface,
  AppointmentInterface,
  ApptPriceDataInterface,
  BookingConfirmation,
  CategoryInterface,
  ServiceInterface,
  ServiceType,
  VisitedServices
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/services.api';

export const bookingApi = createApi({
  reducerPath: 'bookingApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  endpoints: (builder) => ({
    getApptDepositPrefs: builder.query({
      queryFn: async ({ svc_list, locid }, { dispatch }) => {
        try {
          const { data } = await api.getApptDepositPrefs(svc_list, locid);

          let amount = 0;

          if (data?.req_deposit) {
            amount = data?.deposit_amount ?? 0;
          } else if (data?.req_guarantee) {
            amount = data?.guarantee_amount ?? 0;
          }
          dispatch(setDepositAmount(amount));

          return { data };
        } catch (error) {
          console.error(
            `Error while getting deposit preferences for selected services: ${error.message}`
          );

          return { error };
        }
      }
    }),
    getApptConfirmationSettings: builder.query<BookingConfirmation, string>({
      queryFn: async () => {
        try {
          const { data } = await api.getApptConfirmationSettings();

          return { data };
        } catch (error) {
          console.error(
            `Error while getting confirmation settings: ${error.message}`
          );

          return { error };
        }
      }
    }),
    calculateAppointmentPrice: builder.query<
      ApptPriceDataInterface,
      {
        appt: AppointmentInterface;
        locationId: number;
        attendees: number[];
      }
    >({
      queryFn: async ({ appt, locationId, attendees }) => {
        try {
          const { data } = await api.calcApptPrice(appt, locationId, attendees);

          return { data };
        } catch (error) {
          console.error(
            `Error while calculate appointment price: ${error.message}`
          );

          return { error };
        }
      }
    }),
    getCategoriesByLocId: builder.query<CategoryInterface[], number>({
      queryFn: async (locationId) => {
        try {
          const { data } = await api.getCategoriesByLocId(locationId);

          return { data };
        } catch (error) {
          console.error(`Error while getting categories: ${error.message}`);

          return { error };
        }
      }
    }),
    getServicesByLocId: builder.query<
      ServiceInterface[],
      { categoryId: number; locationId: number }
    >({
      queryFn: async ({ categoryId, locationId }, { dispatch }) => {
        dispatch(setIsServicesLoading(true));
        try {
          const { data } = await api.getServicesByLocId(
            categoryId,
            locationId,
            1
          );

          return { data };
        } catch (error) {
          console.error(
            `Error while getting services by location id: ${error.message}`
          );

          return { error };
        } finally {
          dispatch(setIsServicesLoading(false));
        }
      }
    }),
    getServicesList: builder.query<
      ServiceInterface[],
      { locationId: number; type: ServiceType }
    >({
      queryFn: async ({ locationId, type }) => {
        try {
          const { data } = await api.getServicesList(locationId, type);

          return { data };
        } catch (error) {
          console.error(`Error while getting service list: ${error.message}`);

          return { error };
        }
      }
    }),
    getAppointments: builder.query<
      AppointmentDataInterface,
      {
        locid: number;
        service: VisitedServices[];
        date: FormDataWithDateRange | DateRange | null;
        isAdvancedSearch: boolean;
      }
    >({
      queryFn: async ({ locid, service, date, isAdvancedSearch }) => {
        try {
          const params = isAdvancedSearch
            ? generateAdvancedSearchParams(
                locid,
                service,
                date as FormDataWithDateRange
              )
            : generateBasicSearchParams(
                locid,
                service,
                date?.startDate as string,
                date?.endDate
              );

          const { data } = await api.getAppointments(params);

          return { data: data.data };
        } catch (error) {
          console.error(`Error while getting appointments: ${error.message}`);

          return { error: error.response?.data?.error || error.message };
        }
      }
    })
  })
});

export const {
  useGetApptDepositPrefsQuery,
  useGetApptConfirmationSettingsQuery,
  useCalculateAppointmentPriceQuery,
  useGetCategoriesByLocIdQuery,
  useGetServicesByLocIdQuery,
  useGetServicesListQuery,
  useGetAppointmentsQuery
} = bookingApi;
